/**
 * `intervention.*` — THE PREFILL KEYS THAT HAD NO PRODUCER.
 * ========================================================
 *
 * The owner requirement, verbatim:
 *
 * > *"This is something that should pass from the intervention, no tech will know it: Case ID /
 * > Name of the location where you are installing the locker / Kiosk ID / External ID /
 * > installation ID. They can type it in but it's all very long and they wouldn't type it in. So
 * > in general all of the stuff that's on an intervention for installs/surveys etc should pre-fill
 * > from an intervention."*
 *
 * The client wiring for those questions already existed and was already correct: `install.ts`
 * bound `case_id`, `location_name`, `kiosk_id` and `external_id` to prefill sources, and
 * `prefill.ts` had the keys. **They rendered blank because nothing produced a value.** The kiosk
 * half has a producer (`prefillInputsFromKiosk`); the intervention half had none, and
 * `prefillInputsFromKiosk`'s own header said so: *"`intervention.*` — these come from
 * `GET /interventions`, not from a kiosk read."*
 *
 * This module is that producer. It is the intervention twin of `prefillInputsFromKiosk` and of
 * `device.ts`, and it exists as a separate file for the same reason `device.ts` does: a catalog key
 * with no producer is indistinguishable from a missing feature at the point of use.
 *
 * ── WHY THIS MATTERS MORE FOR SURVEYS THAN FOR INSTALLS ──────────────────────────────────────────
 *
 * Surveys are `Intervention__c` rows too — `Intervention_Type__c` in {`Survey`, `Premier Survey`,
 * `Survey Rework`, `Survey for Relocation`, `Survey for Upsize`}, **2,272 of the 25,080 rows** the
 * work-list predicate admits in Dev1. And **50.7% of them have no `Kiosk_ID__c` at all**, because
 * you survey a location before anything is installed on it. So on a survey the kiosk read cannot
 * answer *any* of these questions and the intervention is the only source. Measured on that
 * Survey-type subset (Dev1, 2026-09-03):
 *
 *   | value | fill on Survey-type rows |
 *   |---|---:|
 *   | `Address__c` | 2,272 / 2,272 — 100% |
 *   | `Case_ID__c` | 2,269 / 2,272 — 99.9% |
 *   | `Vendor__c` (the scope boundary) | 2,269 / 2,272 — 99.9% |
 *   | `Account_Name__c` → `intervention.locationName` | 2,204 / 2,272 — **97.0%** |
 *   | `Installation_Id__c` | 2,203 / 2,272 — 97.0% |
 *   | `Kiosk_ID__c` | 1,119 / 2,272 — 49.3% |
 *   | `Post_Install__c` | 88 / 2,272 — 3.9% |
 *   | `Intervention_Sub_Type__c` → `intervention.dispatchReason` | **0 / 2,272 — 0%** |
 *
 * ⚠️ **`Survey__c` — THE OBJECT — IS NOT READ HERE, AND CANNOT BE.** T-53 is right and the reason
 * is worth stating precisely rather than as "no supplier linkage". Measured over its **233,026**
 * Dev1 rows:
 *
 *   · it has **no reference to `Vendor__c`** at all;
 *   · the two supplier-shaped fields are FREE TEXT — `Vendor_Name__c` (`string`, 32,192 rows =
 *     13.8%) and `Supplier_Email__c` (`string`, 377 rows = 0.16%). Scoping a read on a text field
 *     someone typed is the legacy Qualtrics `VendorEmail=` habit that T-12 exists to forbid: *a
 *     query string is not authentication*, and neither is a name;
 *   · the one lookup that COULD carry scope transitively, `Survey__c.Intervention__c`, is filled on
 *     **458 of 233,026 rows — 0.20%**. So even the indirect route reaches 1 row in 500.
 *
 * There is therefore nothing to invent a link out of, and no `Survey__c` read is attempted. What
 * IS scoped, at 99.9%, is the survey *dispatch* — the `Intervention__c` row — and that is what
 * this module reads. Survey *answers* stay out of reach until `Survey__c` gains a real supplier
 * reference, which is a Salesforce data-model change and not a client one.
 */

import type { InterventionListItem } from '@/core/api';
import type { PrefillInputs } from '@/forms';

/**
 * The install form's first question, derived.
 *
 * `Intervention_Type__c` is a 43-value picklist and the install form offers exactly three
 * options, so most types map to nothing — and mapping to nothing is the correct answer for them:
 * `Maintenance` alone is 12,885 of the 25,080-row actionable set, and answering "Installation"
 * for a maintenance job would reroute the entire form on a guess.
 *
 * ⚠️ **`provisionedWithInstallation` was measured `true` on 0 of 185,591 Dev1 rows**, so the
 * `Installation & Provisioning` branch below is unreachable against the data as it stands. It is
 * implemented rather than dropped because the alternative — inferring the combined case from the
 * type picklist — is exactly the guess this function refuses to make, and because a boolean that
 * is false everywhere today is not a boolean that is false forever. A test asserts the branch, so
 * it is not dead code that has never run.
 *
 * `Reinstallation` (68 rows) is deliberately NOT mapped to `Installation`: it is a distinct type,
 * it is not one of the three options, and the technician answering it themselves costs one tap.
 */
export function deriveVisitKind(item: InterventionListItem): string | undefined {
  if (item.provisionedWithInstallation === true) return 'Installation & Provisioning';
  if (item.type === 'Installation') return 'Installation';
  if (item.type === 'Provisioning') return 'Provisioning';
  return undefined;
}

/**
 * `InterventionListItem` → prefill catalog keys.
 *
 * Every key below is a real `PREFILL_SOURCES` key and a test asserts that, because
 * `resolvePrefill` drops an unknown key **silently** — a typo here would leave the field exactly
 * as blank as it was before this file existed, with nothing failing.
 *
 * ── FOUR KEYS ARE DELIBERATELY NOT PRODUCED, AND EACH ABSENCE IS A MEASUREMENT ───────────────────
 *
 *  · **`intervention.provisioningCode`** — `secret: true` in the catalog, and the work list is a
 *    bulk read of up to 100 jobs. A live provisioning code in every row of that response is a
 *    wider exposure than fetching it for the one job being worked, so it is not in
 *    `INTERVENTION_LIST_FIELDS` and there is nothing here to map. 13.58% filled.
 *  · **`intervention.warehouse`** — 9.05% filled, and the field holds an 18-character `Location`
 *    id rather than the warehouse's name. Prefilling an opaque id as a warehouse would be worse
 *    than asking.
 *  · **`Case_ID__c` → a "case id" key** — this is the tempting one and it is a trap.
 *    `Case_ID__c` is filled on **13,580 of 25,080 actionable rows (54.15%)**, twice as often as
 *    the `Case__c` lookup (26.70%), so it looks like the better source for the `case_id` question.
 *    But it is an 18-character Case **record id**: 13,577 of its 13,580 non-blank values begin
 *    `500`, and a 2,000-row sample ordered by `LastModifiedDate` was 100% 18-character ids. The
 *    question asks for a case *number*, its only consumer is a human reading the review page
 *    (`Case__c` is refused by the write allowlist — `infra/src/writer/field-policy.ts` lists it
 *    under `UNCORRECTED_LINK_FIELDS`), and `500Qy0000ABCDEFGHI` tells that human nothing. So
 *    `case.number` is fed from `Case__r.CaseNumber` instead, at 26.70% — a lower fill rate and the
 *    right value. Prefilling something recognisable half as often beats prefilling something
 *    unreadable.
 *  · **`intervention.installationId` IS produced but is bound to no field** — see the caveat on
 *    that catalog entry. 98.93% filled, genuinely the value the owner named, and the only question
 *    whose label mentions it writes to `Vendor_reference_number__c`, which is the *vendor's*
 *    reference rather than Amazon's installation id. It is carried so it is available and
 *    displayed, and not adopted, because mixing the two namespaces in one text field is a
 *    data-quality harm that no override can undo. Splitting that question is an owner decision.
 *
 * ── AND ONE KEY IS SUPPLIED FROM A DIFFERENT OBJECT THAN THE CATALOG NAMES ───────────────────────
 *
 * `kiosk.id`'s catalog mapping is `OpportunityLineItem.Kiosk_ID__c`, and the value here comes from
 * `Intervention__c.Kiosk_ID__c`. That is not a mismatch: the intervention field is a formula,
 * `IF(ISBLANK(Post_Install__c), Case__r.Kiosk_Id__c, Post_Install__r.Kiosk_ID__c)`, read verbatim
 * from the Dev1 describe — it dereferences the very field the catalog names. Same value, second
 * path, 84.78% of the actionable set. This is what removes the KID question, which per PRD §4.3 is
 * the one identifier *"not printed anywhere on the locker"*.
 */
export function prefillInputsFromIntervention(item: InterventionListItem | null): PrefillInputs {
  if (item === null) return {};

  const inputs: Record<string, string | number | boolean> = {};
  const put = (key: string, value: string | number | null | undefined): void => {
    if (value === null || value === undefined) return;
    if (typeof value === 'string' && value.trim() === '') return;
    inputs[key] = value;
  };

  put('intervention.id', item.interventionId);
  put('intervention.type', item.type); // 99.94%
  put('intervention.visitKind', deriveVisitKind(item)); // derivation; undefined for most types
  put('intervention.lockerType', item.lockerClass); // 100.00%
  put('intervention.locationName', item.locationName); // 98.57%; 97.0% on surveys
  put('intervention.installationId', item.installationId); // 98.93%; bound to no field, see above
  put('intervention.postInstallId', item.ticketId); // 72.38%
  put('intervention.dispatchReason', item.subType); // 54.92%; 0% on surveys
  put('intervention.supplierWorkOrderId', item.supplierWorkOrderId); // 0.18% — see the caveat

  /*
   * Two keys the catalog files under `kiosk.*`/`case.*` because that is where the value lives,
   * which the intervention can nevertheless supply. They are put LAST so the reading order matches
   * `FormHost`'s merge order, where a real kiosk read wins any collision.
   */
  put('kiosk.id', item.kioskId); // 84.78%; Intervention__c.Kiosk_ID__c formula, see above
  put('case.number', item.caseNumber); // 26.70%; Case__r.CaseNumber

  return inputs;
}
